import { Check } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

const TIERS = [
  {
    key: "standard",
    name: "Standard Lease",
    features: ["MP3 + WAV files", "100,000 streams", "Non-exclusive rights", "Credit required"],
  },
  {
    key: "pro",
    name: "Professional Lease",
    highlight: true,
    features: ["WAV + Stems (Trackouts)", "500,000 streams", "Music video rights", "Non-exclusive"],
  },
  {
    key: "unlimited",
    name: "Unlimited Covenant",
    features: [
      "WAV + Stems (Trackouts)",
      "Unlimited streams",
      "Music video + Radio",
      "Full commercial use",
    ],
  },
];

export default function LicenseModal({ beat, onClose }) {
  const { addItem, openCart } = useCart();

  const handleSelect = (tier) => {
    addItem(beat, tier);
    toast.success(`${beat.title} — ${tier.charAt(0).toUpperCase() + tier.slice(1)} added to cart`);
    onClose();
    openCart();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        data-testid="license-modal"
        className="chrome-panel max-w-3xl p-0 rounded-2xl overflow-hidden"
      >
        <DialogHeader className="px-8 pt-8 pb-4">
          <DialogTitle
            className="text-2xl text-[#EAEAEA]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Choose Your License
          </DialogTitle>
          <DialogDescription className="text-[#737373] text-sm">
            {beat.title} — {beat.bpm} BPM, {beat.key}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-px bg-[#1A1A1A]">
          {TIERS.map((tier) => (
            <div
              key={tier.key}
              data-testid={`license-tier-${tier.key}`}
              className={`chrome-panel rounded-lg p-6 flex flex-col transition-all gold-glow-hover ${
                tier.highlight ? "ring-1 ring-gold/40" : ""
              }`}
            >
              {tier.highlight && (
                <span className="text-[10px] tracking-[0.2em] uppercase text-gold mb-3">
                  Most Popular
                </span>
              )}
              <h3
                className="text-lg font-medium text-[#EAEAEA] mb-1"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {tier.name}
              </h3>
              <p className="text-3xl font-semibold text-gold mb-5">
                ${beat.pricing[tier.key]}
              </p>
              <ul className="space-y-2 mb-6 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#A1A1A1]">
                    <Check size={14} className="mt-0.5 text-gold flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`select-tier-${tier.key}`}
                onClick={() => handleSelect(tier.key)}
                className={`w-full py-3 text-sm font-semibold tracking-widest uppercase transition-all rounded-full ${
                  tier.highlight
                    ? "metal-gold-surface hover:brightness-105"
                    : "chrome-dark border border-gold/40 text-[#F2D072] hover:border-gold"
                }`}
              >
                Select
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
